'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';

import type { FormOption, TranslateFunction } from '@/types/general';
import { getLanguageOptions } from '@/utils/language-helpers';

import ComboBox from '../ui/ComboBox';
import { Icon } from '../ui/icon';
import FlagByLanguage from './FlagByLanguage';

// Stable default arrays to prevent infinite render loops
const DEFAULT_VALUE: string[] = [];
const DEFAULT_AVAILABLE_LANGUAGES: string[] = [];

export type LanguageMultiSelectorProps = {
  name?: string;
  title?: string;
  description?: string;
  placeholder?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  availableLanguages?: string[];
  allLanguages?: string[];
  loading?: boolean;
  disabled?: boolean;
  className?: string;
};

/**
 * Reusable multi-select language selector component
 * Follows the established patterns from external links modal and course language selector
 * Supports available/unavailable language states with visual indicators
 */
export function LanguageMultiSelector({
  name,
  title,
  description,
  placeholder,
  value = DEFAULT_VALUE,
  onChange,
  availableLanguages = DEFAULT_AVAILABLE_LANGUAGES,
  allLanguages,
  loading = false,
  disabled = false,
  className,
}: LanguageMultiSelectorProps) {
  const translate = useTranslations();

  // Determine which languages to show
  const languagesToShow = useMemo(() => {
    if (allLanguages) {
      // Selected languages outside allLanguages (e.g. a copied course keeping
      // languages the space doesn't support) must still be listed, otherwise
      // their chips fall back to the "unknown option" label.
      const selectedNotListed = value.filter(lang => allLanguages.includes(lang) === false);
      return [...new Set([...allLanguages, ...selectedNotListed])];
    }
    // If no allLanguages provided, use available languages + selected unavailable ones
    const selectedUnavailable = value.filter(lang => availableLanguages.includes(lang) === false);
    return [...new Set([...availableLanguages, ...selectedUnavailable])];
  }, [allLanguages, availableLanguages, value]);

  // Transform languages into options
  const languageOptions = useMemo(() => {
    const baseOptions = getLanguageOptions(translate as TranslateFunction, languagesToShow);

    return baseOptions.map(option => ({
      label: option.name,
      value: option.code,
    }));
  }, [languagesToShow, translate]);

  // Render option with flag
  const renderOption = useCallback((option: FormOption, isSelected: boolean) => {
    return (
      <div className="flex items-center gap-2 w-full">
        <Icon
          icon="material-symbols:check"
          className={isSelected ? 'opacity-100' : 'opacity-0'}
          size={16}
        />
        <FlagByLanguage code={option.value} showLabel={false} className="flex-shrink-0" rounded={false} />
        <span className="font-medium truncate">{option.label}</span>
      </div>
    );
  }, []);

  // Render selected value with flag
  const renderSelectedValue = useCallback((option: FormOption) => {
    return (
      <>
        <FlagByLanguage code={option.value} showLabel={false} className="flex-shrink-0" size="sm" rounded={true} />
        <span className="font-medium truncate">{option.label}</span>
      </>
    );
  }, []);

  return (
    <div className={`space-y-2 ${className}`}>
      <ComboBox
        name={name}
        title={title}
        description={description}
        placeholder={placeholder || translate('course.languages.selectLanguages')}
        options={languageOptions}
        value={value}
        onChange={(newValue) => {
          if (Array.isArray(newValue) && onChange) {
            onChange(newValue);
          }
        }}
        multiple={true}
        disabled={disabled}
        renderOption={renderOption}
        renderSelectedValue={renderSelectedValue}
        loading={loading}
      />
    </div>
  );
}

export default LanguageMultiSelector;
